import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { useClientTranslations } from "../../lib/i18n/client";
import type { CustomerInfo } from "../../services/revenueCatService";
import * as RevenueCatService from "../../services/revenueCatService";
import styles from "./SubscriptionInfo.module.css";

interface SubscriptionDetails {
	tier: "free" | "plus" | "premium";
	isActive: boolean;
	expirationDate?: string;
	willRenew?: boolean;
}

export function SubscriptionInfo() {
	const { t } = useClientTranslations("pricing");
	const { t: tProfile } = useClientTranslations("profile");
	const { logPricing } = useAnalytics();
	const [subscriptionDetails, setSubscriptionDetails] =
		useState<SubscriptionDetails | null>(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		async function loadSubscriptionInfo() {
			try {
				setIsLoading(true);

				// Get customer info from RevenueCat
				const customerInfo: CustomerInfo =
					await RevenueCatService.getCustomerInfo();

				// Check for active entitlements using RevenueCat entitlement identifiers
				const activeEntitlements = customerInfo.entitlements.active || {};

				// Check entitlements in order of priority: premium > plus > pro
				if ("premium" in activeEntitlements) {
					const entitlement = activeEntitlements.premium as {
						expirationDate?: string | null;
						willRenew?: boolean;
					};

					setSubscriptionDetails({
						tier: "premium",
						isActive: true,
						expirationDate: entitlement?.expirationDate || undefined,
						willRenew: entitlement?.willRenew,
					});
				} else if ("plus" in activeEntitlements) {
					const entitlement = activeEntitlements.plus as {
						expirationDate?: string | null;
						willRenew?: boolean;
					};

					setSubscriptionDetails({
						tier: "plus",
						isActive: true,
						expirationDate: entitlement?.expirationDate || undefined,
						willRenew: entitlement?.willRenew,
					});
				} else if ("pro" in activeEntitlements) {
					const entitlement = activeEntitlements.pro as {
						expirationDate?: string | null;
						willRenew?: boolean;
					};

					setSubscriptionDetails({
						tier: "plus",
						isActive: true,
						expirationDate: entitlement?.expirationDate || undefined,
						willRenew: entitlement?.willRenew,
					});
				} else {
					// No active subscription - free tier
					setSubscriptionDetails({
						tier: "free",
						isActive: true,
					});
				}
			} catch (error) {
				console.error("[SubscriptionInfo] Failed to load subscription:", error);
				// Default to free tier on error
				setSubscriptionDetails({
					tier: "free",
					isActive: true,
				});
			} finally {
				setIsLoading(false);
			}
		}

		loadSubscriptionInfo();
	}, []);

	const handleUpgrade = () => {
		logPricing("subscription_upgrade_clicked", {
			current_tier: subscriptionDetails?.tier || "unknown",
		});
		toast.info(tProfile("settings.subscriptionsNotActive"));
	};

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.loading}>
					{t("messages.loadingSubscription")}
				</div>
			</div>
		);
	}

	if (!subscriptionDetails) {
		return null;
	}

	const { tier, willRenew } = subscriptionDetails;
	const isFree = tier === "free";
	const displayName = t(`plans.${tier}.name`);

	return (
		<div className={styles.container}>
			<div className={`${styles.card} ${styles[tier]}`}>
				<div className={styles.tierBadge}>
					<span className={styles.tierName}>{displayName}</span>
					{!isFree && (
						<span className={styles.activeBadge}>
							{t("subscription.active")}
						</span>
					)}
				</div>

				<div className={styles.details}>
					{isFree ? (
						<>
							<p className={styles.description}>
								{t("messages.freeDescription")}
							</p>
							<button
								type="button"
								className={styles.upgradeButton}
								onClick={handleUpgrade}
							>
								{t("buttons.upgradeNow")}
							</button>
						</>
					) : (
						<>
							<div className={styles.detailRow}>
								<span className={styles.detailLabel}>
									{t("subscription.status")}
								</span>
								<span className={styles.detailValue}>
									{willRenew
										? t("subscription.activeAutoRenew")
										: t("subscription.activeExpires")}
								</span>
							</div>

							<button
								type="button"
								className={styles.manageButton}
								onClick={handleUpgrade}
							>
								{t("buttons.manageSubscription")}
							</button>
						</>
					)}
				</div>
			</div>
		</div>
	);
}
